import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
  Stage,
  Layer,
  Rect as KonvaRect,
  Image as KonvaImage,
  Ellipse as KonvaEllipse,
  Line as KonvaLine,
  Arrow as KonvaArrow,
  Transformer,
  Text,
} from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';
import ShapeToolBar from './ShapeToolBar';
import { Arrow, Circle, Config, DrawAction, Note, Rectangle, Scribble } from './types';
import { createImageFromSrc, measureTextSize } from './utils';
import { useUploadInvestorReportChartImageMutation } from '@api/investorReport';
import { useUploadV2ChartImageMutation } from '@api/investorReportV2';
import ControlToolbar from './ControlToolbar';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { popupFooterCancelButtonStyles, popupFooterSaveButtonStyles } from './constants';

interface AnnotateChartPopupProps {
  src: string | null;
  aspectRatio: number; // width / height
  annotationConfig: any;
  open: boolean;
  reportId: string;
  slideId: string;
  clientCode?: string;
  onClose: (
    e: {
      aspectRatio: number;
      annotatedImageFileName: string | null;
      annotationConfig: any;
    } | null,
  ) => void;
}

const EXPORT_PIXEL_RATIO = 10;
const DEFAULT_COLOR = '#162C36';
const DEFAULT_STROKE_WIDTH = 4;
const DEFAULT_FONT_FAMILY = 'Manrope';
const DEFAULT_FONT_SIZE = 18;

const getHeightAndWidthForImage = (aspectRatio: number) => {
  let baseWidth = 1000;
  let baseHeight = 450;

  baseHeight = baseWidth / aspectRatio;

  if (baseHeight > 450) {
    baseHeight = 450;
    baseWidth = baseHeight * aspectRatio;
  }

  return {
    baseHeight,
    baseWidth,
  };
};

const AnnotateChartPopup: React.FC<AnnotateChartPopupProps> = ({
  src,
  reportId,
  slideId,
  clientCode,
  aspectRatio,
  annotationConfig,
  open,
  onClose,
}) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: 1000, height: 600 });
  const [drawAction, setDrawAction] = useState<DrawAction>(DrawAction.Select);
  const [rectangles, setRectangles] = useState<Rectangle[]>([]);
  const [arrows, setArrows] = useState<Arrow[]>([]);
  const [scribbles, setScribbles] = useState<Scribble[]>([]);
  const [circles, setCircles] = useState<Circle[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [selectedElement, setSelectedElement] = useState<{ id: string; type: string } | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // undo redo stack
  const [history, setHistory] = useState<
    {
      rectangles: Rectangle[];
      arrows: Arrow[];
      scribbles: Scribble[];
      circles: Circle[];
      notes: Note[];
    }[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [redoStack, setRedoStack] = useState<typeof history>([]);

  const [config, setConfig] = useState<Config>({
    stroke: DEFAULT_STROKE_WIDTH,
    color: DEFAULT_COLOR,
    lineStyle: 'solid',
  });

  const isPaintRef = useRef(false);
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const currentShapeRef = useRef<string | null>(null);

  useEffect(() => {
    if (annotationConfig) {
      const rectangles = annotationConfig.rectangles || [];
      const circles = annotationConfig.circles || [];
      const arrows = annotationConfig.arrows || [];
      const scribbles = annotationConfig.scribbles || [];
      const texts = annotationConfig.texts || [];

      setRectangles(rectangles);
      setCircles(circles);
      setArrows(arrows);
      setScribbles(scribbles);
      setNotes(texts);
      setHistory((prev) => [
        ...prev,
        {
          rectangles: rectangles,
          arrows: arrows,
          scribbles: scribbles,
          circles: circles,
          notes: texts,
        },
      ]);
    }
  }, [annotationConfig]);

  const isDraggable = drawAction === DrawAction.Select;

  useEffect(() => {
    if (!src) return;
    loadBackgroundImageFromSource(src);
  }, [src]);

  const loadBackgroundImageFromSource = (src: string) => {
    const img = createImageFromSrc(src);
    if (!img) return;
    img.onload = () => {
      const { baseHeight, baseWidth } = getHeightAndWidthForImage(aspectRatio);

      setStageSize({
        width: baseWidth,
        height: baseHeight,
      });
      setImage(img);
    };
  };

  useEffect(() => {
    resetToSolidLineStyle(drawAction);
  }, [drawAction]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Delete' && selectedElement) {
      handleDelete();
    }
  };

  const resetToSolidLineStyle = (drawAction: DrawAction) => {
    const drawActionsAllowingLineStyles = [
      DrawAction.Rectangle,
      DrawAction.Circle,
      DrawAction.Select,
    ];
    if (drawActionsAllowingLineStyles.includes(drawAction)) return;
    setConfig((prev) => ({ ...prev, lineStyle: 'solid' }));
  };

  const [uploadInvestorReportChartImage] = useUploadInvestorReportChartImageMutation();
  const [uploadV2ChartImage] = useUploadV2ChartImageMutation();

  const uploadChartImage = async (image: File) => {
    try {
      if (clientCode) {
        const { data: fileName } = await uploadV2ChartImage({
          clientCode,
          slideId,
          file: image,
        }).unwrap();
        return fileName;
      }

      const { data: fileName } = await uploadInvestorReportChartImage({
        reportId,
        slideId,
        file: image,
      }).unwrap();
      return fileName;
    } catch (error) {
      console.error('Error uploading chart image', error);
    }

    return null;
  };

  const calculateAspectRatio = (): number | null => {
    if (!stageRef.current) return null;
    const stage = stageRef.current;
    const width = stage.width();
    const height = stage.height();

    const aspectRatio = width / height;
    return aspectRatio;
  };

  const prepareAnnotatedImage = (): File => {
    if (!stageRef.current) return new File([], 'annotated.png');

    const uri = stageRef.current.toDataURL({
      pixelRatio: EXPORT_PIXEL_RATIO,
    });

    const arr = uri.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = window.atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);

    return new File([u8arr], `annotated-${Date.now()}.png`, { type: mime });
  };

  const updateRectangle = (id: string, updatedObj: Partial<Rectangle>) => {
    setRectangles((prev) => {
      const updated = prev.map((rect) => (rect.id === id ? { ...rect, ...updatedObj } : rect));

      pushToHistory({ rectangles: updated });
      return updated;
    });
  };

  const updateArrow = (id: string, updatedObj: Partial<Arrow>) => {
    setArrows((prev) => {
      const updated = prev.map((arrow) => (arrow.id === id ? { ...arrow, ...updatedObj } : arrow));
      pushToHistory({ arrows: updated });
      return updated;
    });
  };

  const updateScribble = (id: string, updatedObj: Partial<Scribble>) => {
    setScribbles((prev) => {
      const updated = prev.map((scribble) =>
        scribble.id === id ? { ...scribble, ...updatedObj } : scribble,
      );
      pushToHistory({ scribbles: updated });
      return updated;
    });
  };

  const updateCircle = (id: string, updatedObj: Partial<Circle>) => {
    setCircles((prev) => {
      const updated = prev.map((circle) =>
        circle.id === id ? { ...circle, ...updatedObj } : circle,
      );
      pushToHistory({ circles: updated });
      return updated;
    });
  };

  const updateNote = (id: string, updatedObj: Partial<Note>) => {
    setNotes((prev) => {
      const updated = prev.map((note) => (note.id === id ? { ...note, ...updatedObj } : note));
      pushToHistory({ notes: updated });
      return updated;
    });
    setEditingNote(null);
  };

  const pushToHistory = (updatedData?: {
    rectangles?: Rectangle[];
    arrows?: Arrow[];
    scribbles?: Scribble[];
    circles?: Circle[];
    notes?: Note[];
  }) => {
    setHistory((prev) => [
      ...prev,
      {
        rectangles: updatedData?.rectangles ?? [...rectangles],
        arrows: updatedData?.arrows ?? [...arrows],
        scribbles: updatedData?.scribbles ?? [...scribbles],
        circles: updatedData?.circles ?? [...circles],
        notes: updatedData?.notes ?? [...notes],
      },
    ]);

    setRedoStack([]);
  };

  const handleTextDblClick = (note: Note, e: any) => {
    const textNode = e.target;
    const stage = stageRef.current;
    const textRect = textNode.getClientRect();

    const stageBox = stage.container().getBoundingClientRect();

    setEditingNote({
      ...note,
      // compute absolute screen position relative to Stage container
      x: stageBox.left + textRect.x,
      y: stageBox.top + textRect.y,
      width: textRect.width + 40,
      height: textRect.height + 10,
    });
    transformerRef?.current?.nodes([]);
    setSelectedElement(null);
  };

  const handleDelete = () => {
    const transformer = transformerRef.current;
    if (!transformer) return;

    const nodes = transformer.nodes();
    if (nodes.length === 0) return;

    const id = selectedElement?.id;
    const type = selectedElement?.type;

    switch (type) {
      case 'Circle':
      case 'Ellipse': {
        setCircles((prev) => {
          const updated = prev.filter((circle) => circle.id !== id);
          pushToHistory({ circles: updated });
          return updated;
        });
        break;
      }
      case 'Rect': {
        setRectangles((prev) => {
          const updated = prev.filter((rect) => rect.id !== id);
          pushToHistory({ rectangles: updated });
          return updated;
        });
        break;
      }
      case 'Line': {
        setScribbles((prev) => {
          const updated = prev.filter((scribble) => scribble.id !== id);
          pushToHistory({ scribbles: updated });
          return updated;
        });
        break;
      }
      case 'Arrow': {
        setArrows((prev) => {
          const updated = prev.filter((arrow) => arrow.id !== id);
          pushToHistory({ arrows: updated });
          return updated;
        });
        break;
      }
      case 'Text': {
        setNotes((prev) => {
          const updated = prev.filter((note) => note.id !== id);
          pushToHistory({ notes: updated });
          return updated;
        });
        break;
      }
      default:
        console.warn(`Unknown shape type: ${type}`);
    }

    transformer.nodes([]);
    setSelectedElement(null);
  };
  const handleUndo = () => {
    setHistory((prev) => {
      if (prev.length < 2) return prev;

      const lastState = prev[prev.length - 2];
      const currentState = prev[prev.length - 1];
      setRectangles(lastState.rectangles);
      setArrows(lastState.arrows);
      setScribbles(lastState.scribbles);
      setCircles(lastState.circles);
      setNotes(lastState.notes);

      // Push current state to redo stack
      setRedoStack((redoPrev) => [
        ...redoPrev,
        {
          rectangles: currentState.rectangles,
          arrows: currentState.arrows,
          scribbles: currentState.scribbles,
          circles: currentState.circles,
          notes: currentState.notes,
        },
      ]);

      return prev.slice(0, prev.length - 1);
    });
  };

  const handleRedo = () => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;

      const lastRedo = prev[prev.length - 1];

      setRectangles(lastRedo.rectangles);
      setArrows(lastRedo.arrows);
      setScribbles(lastRedo.scribbles);
      setCircles(lastRedo.circles);
      setNotes(lastRedo.notes);

      setHistory((historyPrev) => [
        ...historyPrev,
        {
          rectangles: lastRedo.rectangles,
          arrows: lastRedo.arrows,
          scribbles: lastRedo.scribbles,
          circles: lastRedo.circles,
          notes: lastRedo.notes,
        },
      ]);

      return prev.slice(0, prev.length - 1); // remove last redo
    });
  };

  const onStageMouseUp = (e: KonvaEventObject<MouseEvent>) => {
    if (drawAction !== DrawAction.Select) {
      pushToHistory();
    }
    if (drawAction === DrawAction.Scribble) {
      isPaintRef.current = false;
      return;
    }

    if (drawAction === DrawAction.Select || drawAction === DrawAction.Text) return;

    isPaintRef.current = false;

    setDrawAction(DrawAction.Select);
  };

  const onMouseUpElement = (e: any) => {
    const currentTarget = e.currentTarget;

    const id = currentTarget.id();
    const type = currentTarget.className;
    setSelectedElement({ id, type });
    transformerRef?.current?.node(currentTarget);
  };

  const onStageMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (drawAction === DrawAction.Select) return;

    isPaintRef.current = true;
    const stage = stageRef?.current;
    const pos = stage?.getPointerPosition();
    const x = pos?.x || 0;
    const y = pos?.y || 0;
    const id = uuidv4();

    currentShapeRef.current = id;

    switch (drawAction) {
      case DrawAction.Scribble: {
        setScribbles((prevScribbles) => [
          ...prevScribbles,
          {
            id,
            points: [x, y],
            color: config.color,
            strokeWidth: config.stroke,
          },
        ]);
        break;
      }
      case DrawAction.Circle: {
        setCircles((prevCircles) => [
          ...prevCircles,
          {
            id,
            radius: 1,
            radiusX: 1,
            radiusY: 1,
            x,
            y,
            color: config.color,
            strokeWidth: config.stroke,
            lineStyle: config.lineStyle,
          },
        ]);
        break;
      }
      case DrawAction.Rectangle: {
        setRectangles((prevRectangles) => [
          ...prevRectangles,
          {
            id,
            height: 1,
            width: 1,
            x,
            y,
            color: config.color,
            strokeWidth: config.stroke,
            lineStyle: config.lineStyle,
          },
        ]);
        break;
      }
      case DrawAction.Arrow: {
        setArrows((prevArrows) => [
          ...prevArrows,
          {
            id,
            points: [x, y, x, y],
            color: config.color,
            strokeWidth: config.stroke,
            lineStyle: config.lineStyle,
          },
        ]);
        break;
      }
      case DrawAction.Text:
        {
          const { width, height } = measureTextSize(
            'Enter Text',
            DEFAULT_FONT_SIZE,
            DEFAULT_FONT_FAMILY,
          );

          setNotes((prevText) => {
            const updated = [
              ...prevText,
              {
                id,
                text: 'Enter text',

                x: x,
                y: y,
                height,
                width,
                color: config.color,
              },
            ];
            pushToHistory({ notes: updated });
            return updated;
          });
        }
        setDrawAction(DrawAction.Select);
    }
  };

  const onStageMouseMove = () => {
    if (drawAction === DrawAction.Select || !isPaintRef.current) return;

    const stage = stageRef?.current;
    const id = currentShapeRef.current;
    const pos = stage?.getPointerPosition();
    const x = pos?.x || 0;
    const y = pos?.y || 0;

    switch (drawAction) {
      case DrawAction.Scribble: {
        setScribbles((prevScribbles) =>
          prevScribbles?.map((prevScribble) =>
            prevScribble.id === id
              ? {
                  ...prevScribble,
                  points: [...prevScribble.points, x, y],
                }
              : prevScribble,
          ),
        );
        break;
      }
      case DrawAction.Circle: {
        setCircles((prevCircles) =>
          prevCircles?.map((prevCircle) =>
            prevCircle.id === id
              ? {
                  ...prevCircle,
                  radius: Math.max(Math.abs(x - prevCircle.x), Math.abs(y - prevCircle.y)),
                  radiusX: Math.abs(x - prevCircle.x),
                  radiusY: Math.abs(y - prevCircle.y),
                }
              : prevCircle,
          ),
        );
        break;
      }
      case DrawAction.Rectangle: {
        setRectangles((prevRectangles) =>
          prevRectangles?.map((prevRectangle) =>
            prevRectangle.id === id
              ? {
                  ...prevRectangle,
                  height: y - prevRectangle.y,
                  width: x - prevRectangle.x,
                }
              : prevRectangle,
          ),
        );
        break;
      }
      case DrawAction.Arrow: {
        setArrows((prevArrows) =>
          prevArrows.map((prevArrow) =>
            prevArrow.id === id
              ? {
                  ...prevArrow,
                  points: [prevArrow.points[0], prevArrow.points[1], x, y],
                }
              : prevArrow,
          ),
        );
        break;
      }
    }
  };

  const onShapeClick = (e: KonvaEventObject<MouseEvent>) => {
    if (drawAction !== DrawAction.Select) return;
    const currentTarget = e.currentTarget;
    const id = currentTarget.id();
    const type = currentTarget.className;
    setSelectedElement({ id, type });
    transformerRef?.current?.node(currentTarget);
  };

  const onBgClick = (e: KonvaEventObject<MouseEvent>) => {
    transformerRef?.current?.nodes([]);
    setSelectedElement(null);
  };

  const handleClickOutsideCanvas = (e: React.MouseEvent<HTMLDivElement> | undefined) => {
    if (stageRef.current?.container().contains(e?.target as Node)) {
      return;
    }
    transformerRef?.current?.nodes([]);
    setSelectedElement(null);
  };

  const onSaveClick = async () => {
    setIsSaving(true);
    transformerRef?.current?.nodes([]);
    setSelectedElement(null);
    const annotatedImage = prepareAnnotatedImage();
    const annotatedImageFileName = await uploadChartImage(annotatedImage);
    const calculatedAspectRatio = calculateAspectRatio();

    /* Defensive check: if either image failed to upload, don't continue */
    if (!annotatedImageFileName) {
      console.error('Error uploading chart images');
      return;
    }
    if (!calculatedAspectRatio) return;
    const annotationConfig = JSON.stringify({
      texts: notes,
      rectangles: rectangles,
      circles: circles,
      arrows: arrows,
      scribbles: scribbles,
    });

    /* Close the popup with filename for annotated and raw image */
    onClose({
      aspectRatio: calculatedAspectRatio,
      annotatedImageFileName: annotatedImageFileName,
      annotationConfig,
    });
  };

  return (
    <Dialog
      onClose={(e) => onClose(null)}
      open={open}
      PaperProps={{
        sx: {
          width: '1298px',
          height: '742px',
          maxWidth: 'none', // disables MUI's default responsive maxWidth
        },
      }}
      onClick={handleClickOutsideCanvas}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{
            fontSize: '22px',
            lineHeight: '30px',
            fontWeight: 700,
            color: '#162936',
          }}
        >
          Chart Annotation
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 500,
            color: '#6C6C6C',
          }}
        >
          Use the tool bar to annotate, comment and detail the graph
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          backgroundColor: '#F0F0F1',
          pointerEvents: isSaving ? 'none' : 'auto',
          opacity: isSaving ? 0.6 : 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '0.875rem',
          }}
        >
          <ShapeToolBar
            drawAction={drawAction}
            setDrawAction={setDrawAction}
            config={config}
            setConfig={setConfig}
          />{' '}
          <ControlToolbar
            handleDelete={handleDelete}
            isDisabled={selectedElement === null}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stage
            ref={stageRef}
            style={{
              border: '1px solid rgb(103, 144, 172)',
              cursor: isPaintRef.current ? 'move' : 'default',
            }}
            width={stageSize.width}
            height={stageSize.height}
            onMouseUp={onStageMouseUp}
            onMouseDown={onStageMouseDown}
            onMouseMove={onStageMouseMove}
          >
            <Layer>
              <KonvaRect
                x={0}
                y={0}
                width={stageSize.width}
                height={stageSize.height}
                fill="white"
                id="bg"
              />
              {image && (
                <KonvaImage
                  image={image}
                  x={0}
                  y={0}
                  width={stageSize.width}
                  height={stageSize.height}
                  onClick={onBgClick}
                />
              )}
              {arrows.map((arrow) => (
                <KonvaArrow
                  key={arrow.id}
                  id={arrow.id}
                  points={arrow.points}
                  fill={arrow.color}
                  stroke={arrow.color}
                  strokeWidth={arrow.strokeWidth}
                  onClick={onShapeClick}
                  draggable={isDraggable}
                  onMouseUp={(e: any) => onMouseUpElement(e)}
                  onDragEnd={(e) => {
                    const node = e.target;
                    const dx = node.x();
                    const dy = node.y();
                    node.x(0);
                    node.y(0);
                    updateArrow(arrow.id, {
                      points: [
                        arrow.points[0] + dx,
                        arrow.points[1] + dy,
                        arrow.points[2] + dx,
                        arrow.points[3] + dy,
                      ],
                    });
                  }}
                  onTransformEnd={(e) => {
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();

                    const { x: offsetX, y: offsetY } = node.position();

                    // Reset transform for consistent data
                    node.scaleX(1);
                    node.scaleY(1);
                    node.position({ x: 0, y: 0 });

                    const points = arrow.points;
                    const [x1, y1, x2, y2] = points;

                    // Apply both scaling and translation
                    const newPoints = [
                      x1 * scaleX + offsetX,
                      y1 * scaleY + offsetY,
                      x2 * scaleX + offsetX,
                      y2 * scaleY + offsetY,
                    ];

                    updateArrow(arrow.id, { points: newPoints });
                  }}
                />
              ))}
              {rectangles.map((rectangle) => (
                <KonvaRect
                  key={rectangle.id}
                  x={rectangle?.x}
                  y={rectangle?.y}
                  height={rectangle?.height}
                  width={rectangle?.width}
                  stroke={rectangle?.color}
                  id={rectangle?.id}
                  strokeWidth={rectangle.strokeWidth}
                  dash={rectangle.lineStyle === 'dashed' ? [10, 5] : []}
                  onClick={onShapeClick}
                  draggable={isDraggable}
                  onMouseUp={(e: any) => onMouseUpElement(e)}
                  onDragEnd={(e) => {
                    updateRectangle(rectangle.id, {
                      x: e.target.x(),
                      y: e.target.y(),
                    });
                  }}
                  onTransformEnd={(e) => {
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    node.scaleX(1);
                    node.scaleY(1);
                    updateRectangle(rectangle.id, {
                      x: node.x(),
                      y: node.y(),
                      width: Math.max(5, node.width() * scaleX),
                      height: Math.max(5, node.height() * scaleY),
                    });
                  }}
                />
              ))}
              {circles.map((circle) => (
                <KonvaEllipse
                  key={circle.id}
                  id={circle.id}
                  x={circle?.x}
                  y={circle?.y}
                  radiusX={circle.radiusX || circle.radius}
                  radiusY={circle.radiusY || circle.radius}
                  stroke={circle?.color}
                  strokeWidth={circle.strokeWidth}
                  dash={circle.lineStyle === 'dashed' ? [10, 5] : []}
                  onClick={onShapeClick}
                  draggable={isDraggable}
                  onMouseUp={(e) => onMouseUpElement(e)}
                  onDragEnd={(e) => {
                    updateCircle(circle.id, {
                      x: e.target.x(),
                      y: e.target.y(),
                    });
                  }}
                  onTransformEnd={(e) => {
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    updateCircle(circle.id, {
                      x: node.x(),
                      y: node.y(),
                      radiusX: (circle.radiusX || circle.radius) * scaleX,
                      radiusY: (circle.radiusY || circle.radius) * scaleY,
                      radius: Math.max(
                        (circle.radiusX || circle.radius) * scaleX,
                        (circle.radiusY || circle.radius) * scaleY,
                      ),
                    });
                    node.scaleX(1);
                    node.scaleY(1);
                  }}
                />
              ))}
              {scribbles.map((scribble) => (
                <KonvaLine
                  key={scribble.id}
                  id={scribble.id}
                  lineCap="round"
                  lineJoin="round"
                  stroke={scribble?.color}
                  strokeWidth={scribble.strokeWidth}
                  points={scribble.points}
                  onClick={onShapeClick}
                  draggable={isDraggable}
                  onDragEnd={(e) => {
                    const node = e.target;
                    const dx = node.x();
                    const dy = node.y();
                    node.x(0);
                    node.y(0);
                    updateScribble(scribble.id, {
                      points: scribble.points.map((p, i) => (i % 2 === 0 ? p + dx : p + dy)),
                    });
                  }}
                  onTransformEnd={(e) => {
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    const { x: offsetX, y: offsetY } = node.position();

                    node.scaleX(1);
                    node.scaleY(1);
                    node.position({ x: 0, y: 0 });

                    const oldPoints = scribble.points;

                    const newPoints: number[] = [];
                    for (let i = 0; i < oldPoints.length; i += 2) {
                      const newX = oldPoints[i] * scaleX + offsetX;
                      const newY = oldPoints[i + 1] * scaleY + offsetY;
                      newPoints.push(newX, newY);
                    }

                    updateScribble(scribble.id, {
                      points: newPoints,
                    });
                  }}
                />
              ))}
              {notes.map((note) => (
                <Text
                  key={note.id}
                  id={note.id}
                  x={note.x}
                  y={note.y}
                  width={note.width}
                  height={note.height}
                  text={note.text}
                  fontSize={DEFAULT_FONT_SIZE}
                  fontFamily={DEFAULT_FONT_FAMILY}
                  fill={note.color}
                  align="center"
                  onClick={onShapeClick}
                  draggable={isDraggable}
                  onDblClick={(e) => handleTextDblClick(note, e)}
                  onDragEnd={(e) => {
                    updateNote(note.id, {
                      x: e.target.x(),
                      y: e.target.y(),
                    });
                  }}
                />
              ))}
              <Transformer
                ref={transformerRef}
                enabledAnchors={selectedElement?.type === 'Text' ? [] : undefined}
              />
            </Layer>
          </Stage>
        </Box>
        {editingNote && (
          <textarea
            autoFocus
            defaultValue={editingNote.text}
            style={{
              position: 'fixed',
              top: editingNote.y,
              left: editingNote.x,
              width: editingNote.width,
              height: editingNote.height,
              color: editingNote.color,
              fontSize: DEFAULT_FONT_SIZE,
              fontFamily: DEFAULT_FONT_FAMILY,
              border: '1px solid #ccc',
              padding: '4px',
              margin: 0,
              background: 'white',
              outline: 'none',
              resize: 'none',
              overflow: 'hidden',
            }}
            onBlur={(e) => {
              const text = e.target.value;
              const { width, height } = measureTextSize(
                text,
                DEFAULT_FONT_SIZE,
                DEFAULT_FONT_FAMILY,
              );

              updateNote(editingNote.id, { text, height, width });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                const text = (e.target as HTMLTextAreaElement).value;
                const { width, height } = measureTextSize(
                  text,
                  DEFAULT_FONT_SIZE,
                  DEFAULT_FONT_FAMILY,
                );
                updateNote(editingNote.id, { text, height, width });
              }
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '1rem',
            pr: '28px',
            py: '0.875rem',
          }}
        >
          <Button
            disableFocusRipple
            disableElevation
            disableTouchRipple
            disableRipple
            onClick={() => onClose(null)}
            variant="outlined"
            color="primary"
            sx={popupFooterCancelButtonStyles}
          >
            Cancel
          </Button>
          <Button
            disableFocusRipple
            disableElevation
            disableTouchRipple
            disableRipple
            onClick={onSaveClick}
            variant="contained"
            color="primary"
            startIcon={<SaveOutlinedIcon />}
            disabled={isSaving}
            sx={popupFooterSaveButtonStyles}
          >
            {isSaving ? 'Saving' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AnnotateChartPopup;
